/**
 * FileList — S3 file browser (input or output).
 * Same UX: list from bucket root for the type, folders + breadcrumbs + files.
 */
import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { getRestApiBearerToken } from '@/services/apiToken';
import { motion } from 'framer-motion';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { useAppStore } from '@/stores/useAppStore';
import { formatRelativeTime, formatFileSize } from '@/utils/formatDate';
import { parseMemoryFileContext, deriveMemorySegment } from '@/utils/memorySyncUtils';
import { useMemorySegmentController } from '@/hooks/useMemorySegmentController';
import MemorySegmentedControl from './MemorySegmentedControl';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

// Folder icon (Lucide-style)
const FolderIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12a2.25 2.25 0 012.25-2.25h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
  </svg>
);

// File icon (document)
const FileIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const FileList = forwardRef(function FileList(
  {
    networkId,
    type = 'input',
    compact = false,
    hideHeader = false,
    onPrefixChange,
    enableMemorySync = false,
    memorySeed = null
  },
  ref
) {
  const { user } = useAuth();
  const connectionId = useAppStore((s) => s.connectionId);
  const idToken = getRestApiBearerToken();

  const { byKey: memoryByKey, selectSegment } = useMemorySegmentController({
    idToken,
    networkId: enableMemorySync && type === 'input' ? networkId : null,
    connectionId: enableMemorySync && type === 'input' ? connectionId : null
  });
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentPrefix, setCurrentPrefix] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const fetchFiles = useCallback(
    async (prefix) => {
      if (!networkId) return;
      setLoading(true);
      setError(null);
      try {
        const idToken = getRestApiBearerToken();
        const params = new URLSearchParams({ type });
        if (prefix) params.set('prefix', prefix);
        const res = await fetch(`${API_BASE_URL}/networks/${networkId}/files?${params}`, {
          headers: { Authorization: `Bearer ${idToken}` }
        });
        if (!res.ok) throw new Error(res.statusText);
        const data = await res.json();
        setFiles(data.files || []);
        setFolders(data.folders || []);
        setCurrentPrefix(data.currentPrefix || '');
      } catch (e) {
        setError(e.message);
        setFiles([]);
        setFolders([]);
      } finally {
        setLoading(false);
      }
    },
    [networkId, type, user?.idToken]
  );

  useEffect(() => {
    fetchFiles(currentPrefix);
  }, [fetchFiles, currentPrefix]);

  useEffect(() => {
    onPrefixChange?.(currentPrefix);
  }, [currentPrefix, onPrefixChange]);

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => fetchFiles(currentPrefix),
      getCurrentPrefix: () => currentPrefix,
      navigateToPrefix: (prefix) => setCurrentPrefix(prefix || '')
    }),
    [fetchFiles, currentPrefix]
  );

  const handleFolderClick = (folder) => {
    const name = folderName(folder);
    const newPrefix = currentPrefix ? `${currentPrefix}${name}/` : `${name}/`;
    setCurrentPrefix(newPrefix);
  };

  const handleBreadcrumbClick = (idx) => {
    const parts = currentPrefix.replace(/\/$/, '').split('/').filter(Boolean);
    if (idx < 0) {
      setCurrentPrefix('');
      return;
    }
    const newPrefix = parts.slice(0, idx + 1).join('/') + '/';
    setCurrentPrefix(newPrefix);
  };

  const breadcrumbParts = currentPrefix ? currentPrefix.replace(/\/$/, '').split('/').filter(Boolean) : [];

  const handleDownload = async (file) => {
    setDownloading(file.key);
    try {
      const idToken = getRestApiBearerToken();
      const res = await fetch(
        `${API_BASE_URL}/networks/${networkId}/files/download?key=${encodeURIComponent(file.key)}`,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      if (!res.ok) throw new Error(res.statusText);
      const { downloadUrl } = await res.json();
      window.open(downloadUrl, '_blank', 'noopener');
    } catch (e) {
      console.error('Download failed:', e);
    } finally {
      setDownloading(null);
    }
  };

  const emptyMsg = breadcrumbParts.length > 0
    ? 'This folder is empty.'
    : type === 'input'
      ? 'No files uploaded yet. Upload below or create a folder.'
      : 'No output files yet. Run the network to generate results.';

  const folderName = (f) => (typeof f === 'string' ? f : f?.name ?? '');
  const folderTime = (f) => (typeof f === 'object' && f != null ? f.lastModified : null);

  return (
    <div className={`flex flex-col ${compact ? '' : 'flex-1 min-h-0'}`}>
      {!hideHeader && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50/80">
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{type === 'input' ? 'Input Files' : 'Output Files'}</span>
          <button
            onClick={() => fetchFiles(currentPrefix)}
            disabled={loading}
            className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      )}

      {breadcrumbParts.length > 0 && (
        <div className="flex items-center gap-1 px-4 py-2 border-b border-gray-100 bg-gray-50/50 text-xs">
          <button
            type="button"
            onClick={() => handleBreadcrumbClick(-1)}
            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
          >
            {type === 'input' ? 'input' : 'output'}
          </button>
          {breadcrumbParts.map((part, idx) => (
            <span key={idx} className="flex items-center gap-1">
              <span className="text-gray-400">/</span>
              <button
                type="button"
                onClick={() => handleBreadcrumbClick(idx)}
                className={`${idx < breadcrumbParts.length - 1 ? 'text-blue-600 hover:text-blue-800 hover:underline font-medium' : 'text-gray-700 font-medium cursor-default'}`}
              >
                {part}
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto">
        {loading && files.length === 0 && folders.length === 0 ? (
          <div className="p-6 text-center">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-xs text-gray-500">Loading...</p>
          </div>
        ) : error ? (
          <div className="p-4 text-center text-sm text-red-600">{error}</div>
        ) : files.length === 0 && folders.length === 0 ? (
          <div className="p-6 text-center">
            <svg className="w-10 h-10 mx-auto mb-2 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm text-gray-500">{emptyMsg}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {folders.map((folder, i) => {
              const name = folderName(folder);
              const lm = folderTime(folder);
              return (
                <motion.div
                  key={`folder-${name}`}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                  onClick={() => handleFolderClick(folder)}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 cursor-pointer group"
                >
                  <FolderIcon className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate group-hover:text-blue-700" title={name}>
                      {name}/
                    </p>
                    {lm ? (
                      <p className="text-xs text-gray-500 mt-0.5">{formatRelativeTime(lm)}</p>
                    ) : null}
                  </div>
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </motion.div>
              );
            })}
            {files.map((file, i) => {
              const memCtx =
                enableMemorySync && type === 'input' ? parseMemoryFileContext(file.key) : null;
              const mem = memCtx ? memoryByKey[file.key] : null;
              const { segment: memorySegment, loading: memoryLoading } = memCtx
                ? deriveMemorySegment(mem, memorySeed, memCtx.agentId, memCtx.documentId)
                : { segment: 'off', loading: false };

              return (
                <motion.div
                  key={file.key}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (folders.length + i) * 0.02 }}
                  className="flex items-start sm:items-center gap-2 px-3 sm:px-4 py-2.5 hover:bg-gray-50 group"
                >
                  <FileIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate" title={file.filename}>
                      {file.filename}
                    </p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>
                      <span className="text-xs text-gray-400">{formatRelativeTime(file.lastModified)}</span>
                    </div>
                  </div>
                  {memCtx && file.filename && (
                    <div className="flex-shrink-0 border-l border-gray-100 pl-2 ml-0.5 py-0.5">
                      <MemorySegmentedControl
                        value={memorySegment}
                        loading={memoryLoading}
                        disabled={!idToken}
                        idPrefix={`mem-${file.key.replace(/[^a-zA-Z0-9_-]/g, '_')}`}
                        onChange={(next) => {
                          if (next === memorySegment) return;
                          selectSegment(
                            file.key,
                            memCtx.agentId,
                            memCtx.documentId,
                            memorySegment,
                            next
                          );
                        }}
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDownload(file)}
                    disabled={downloading === file.key}
                    className="flex-shrink-0 p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-all disabled:opacity-50 self-center"
                    title="Download"
                  >
                    {downloading === file.key ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    )}
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
});

export default FileList;
